<template>
    <section class="card ranking-header-section">
        <div class="header-main-row">
            <div class="stats-container">
                <div class="stat-item">
                    <span class="label">{{ $trans("web_skill_ranking.competence") }}:</span>
                    <span class="value">{{ WebSkillRankingStore.ranking.skill_name || "-" }}</span>
                </div>

                <div class="stat-item">
                    <span class="label">{{ $trans("web_skill_ranking.pool_members") }}:</span>
                    <span class="value">{{ WebSkillRankingStore.ranking.pool_members_count }}</span>
                </div>

                <div class="stat-item">
                    <span class="label">{{ $trans("web_skill_ranking.assigned_records") }}:</span>
                    <span class="value">{{ WebSkillRankingStore.ranking.assigned_records_count }}</span>
                </div>

                <div class="stat-item">
                    <span class="label">{{ $trans("web_skill_ranking.avg_duration") }}:</span>
                    <span class="value">{{ WebSkillRankingStore.ranking.avg_duration ? WebSkillRankingStore.ranking.avg_duration + ' ' + $trans('web_skill_ranking.years') : "-" }}</span>
                </div>

                <div class="stat-item">
                    <span class="label">{{ $trans("web_skill_ranking.last_published_on") }}:</span>
                    <span class="value">{{ WebSkillRankingStore.ranking.last_published || "-" }}</span>
                </div>

                <div class="stat-item">
                    <span class="label">{{ $trans("web_skill_ranking.status") }}:</span>
                    <span class="value" :class="statusClass">
                        {{ $trans("web_skill_ranking.status_types." + WebSkillRankingStore.ranking.status) || WebSkillRankingStore.ranking.status }}
                    </span>
                    <img
                        v-if="WebSkillRankingStore.ranking.status === 'sent' && WebSkillRankingStore.ranking.path"
                        src="/media/images/drupal_reference_icon.png"
                        alt="Open Path"
                        class="drupal-icon clickable ml-2"
                        @click="openRankingPath"
                        :title="$trans('web_skill_ranking.open_path') || 'Open path'"
                    />
                </div>
            </div>

            <div class="button-group">
                <LogButton :onClick="handleLogsClick" />
                
                <el-button
                    v-if="WebSkillRankingStore.ranking.has_available_changes"
                    type="warning"
                    class="recalculate-btn"
                    @click="handleRecalculateClick"
                    :loading="WebSkillRankingStore.loading"
                >
                    {{ $trans("web_skill_ranking.recalculate") || "Recalculate" }}
                </el-button>

                <el-button
                    class="request-publish-btn"
                    type="primary"
                    @click="handleRequestPublicationClick"
                >
                    {{ $trans("web_skill_ranking.request_publication") }}
                </el-button>
            </div>
        </div>

        <div class="info-row">
            <div class="info-item">
                <strong class="label-with-tooltip">
                    {{ $trans("web_skill_ranking.industry_sector") }}*
                    <el-tooltip placement="top" effect="light">
                        <template #content>
                            <div class="tooltip-content">
                                {{ $trans("web_skill_ranking.industry_tooltip") }}
                            </div>
                        </template>
                        <el-icon class="info-icon"><InfoFilled /></el-icon>
                    </el-tooltip>
                    :&nbsp;
                </strong>

                <span v-if="WebSkillRankingStore.ranking.domains?.vertical_name">
                    {{ WebSkillRankingStore.ranking.domains.vertical_name }}
                </span>
                <span v-else class="empty-text">
                    {{ $trans("web_skill_ranking.please_select_verticals") }}
                </span>

                <EditButton
                    :onClick="WebSkillRankingStore.openEditVerticalModal"
                    class="ml-2"
                />
            </div>

            <div
                class="info-item"
                :class="{
                    'opacity-50': !WebSkillRankingStore.ranking.domains?.vertical_name,
                }"
            >
                <strong class="label-with-tooltip">
                    {{ $trans("web_skill_ranking.area_of_expertise") }}
                    <el-tooltip placement="top" effect="light">
                        <template #content>
                            <div class="tooltip-content">
                                {{ $trans("web_skill_ranking.aoe_tooltip") }}
                            </div>
                        </template>
                        <el-icon class="info-icon"><InfoFilled /></el-icon>
                    </el-tooltip>
                    :&nbsp;
                </strong>

                <span
                    v-if="!WebSkillRankingStore.ranking.domains?.vertical_name"
                    class="empty-text"
                >
                    {{ $trans("web_skill_ranking.please_select_aoe_vertical_first") }}
                </span>
                <span v-else-if="WebSkillRankingStore.ranking.domains?.aoe_name">
                    {{ WebSkillRankingStore.ranking.domains.aoe_name }}
                </span>
                <span v-else class="empty-text">
                    {{ $trans("web_skill_ranking.please_select_aoe") }}
                </span>

                <EditButton :onClick="handleAoEEdit" class="ml-2" />
            </div>

            <div class="info-item">
                <strong class="label-with-tooltip">
                    {{ $trans("web_skill_ranking.industry") }}*
                    <el-tooltip placement="top" effect="light">
                        <template #content>
                            <div class="tooltip-content">
                                {{ $trans("web_skill_ranking.industry_tooltip_desc") }}
                            </div>
                        </template>
                        <el-icon class="info-icon"><InfoFilled /></el-icon>
                    </el-tooltip>
                    :&nbsp;
                </strong>

                <span v-if="WebSkillRankingStore.ranking.domains?.industry_name">
                    {{ WebSkillRankingStore.ranking.domains.industry_name }}
                </span>
                <span v-else class="empty-text">
                    {{ $trans("web_skill_ranking.please_select_industry") }}
                </span>

                <EditButton
                    :onClick="WebSkillRankingStore.openEditIndustryModal"
                    class="ml-2"
                />
            </div>
        </div>

        <EditRankingVerticalModal />
        <EditRankingAoEModal />
        <EditRankingIndustryModal />

        <SubmitRankingPublicationModal
            v-if="showPublicationModal"
            v-model="showPublicationModal"
            @confirm="handlePublicationConfirm"
        />
    </section>
</template>

<script>
import { mapStores } from "pinia";
import { useWebSkillRankingStore } from "@/stores/WebSkillRankingStore";
import { InfoFilled } from "@element-plus/icons-vue";
import LogButton from "./../../WebProfile/Components/LogButton.vue";
import EditButton from "./../../WebProfile/Components/EditButton.vue";
import EditRankingVerticalModal from "./../Modals/EditRankingVerticalModal.vue";
import EditRankingAoEModal from "./../Modals/EditRankingAoEModal.vue";
import EditRankingIndustryModal from "./../Modals/EditRankingIndustryModal.vue";
import SubmitRankingPublicationModal from "./../Modals/SubmitRankingPublicationModal.vue";

export default {
    name: "WebSkillRankingHeader",
    components: {
        LogButton,
        EditButton,
        InfoFilled,
        EditRankingVerticalModal,
        EditRankingAoEModal,
        EditRankingIndustryModal,
        SubmitRankingPublicationModal,
    },
    computed: {
        ...mapStores(useWebSkillRankingStore),
        showPublicationModal: {
            get() {
                return this.WebSkillRankingStore.isSubmitPublicationModalOpen;
            },
            set(val) {
                this.WebSkillRankingStore.isSubmitPublicationModalOpen = val;
            },
        },
        statusClass() {
            const status = this.WebSkillRankingStore.ranking.status;
            if (status === "sent") return "status-sent";
            if (status === "prequalified") return "status-prequalified";
            return "status-generated";
        },
    },
    methods: {
        handleLogsClick() {
            const ranking = this.WebSkillRankingStore.ranking;
            const rankingId = ranking.id;
            const skillName = ranking.skill_name || "Ranking";

            if (typeof window.systemLogsModule === "function") {
                const filterCheckbox = document.querySelector(
                    "#checkbox_system-logs-table_milestones_available_1",
                );

                if (filterCheckbox && filterCheckbox.checked) {
                    filterCheckbox.click();
                }

                window
                    .systemLogsModule("web_skill_ranking", rankingId)
                    .setModelNameInSubtitle(skillName)
                    .loadData(false)
                    .showLogsModal();
            } else {
                console.error(
                    "systemLogsModule is not defined in the window object",
                );
            }
        },
        async handleRecalculateClick() {
            try {
                this.$functions?.showLoader();
                await this.WebSkillRankingStore.recalculateRanking();
                this.$message.success(
                    this.$trans("web_skill_ranking.recalculation_success") || "Ranking recalculated successfully"
                );
                setTimeout(() => window.location.reload(), 1000);
            } catch (error) {
                this.$message.error(
                    this.$trans("web_skill_ranking.something_went_wrong") || "Error"
                );
            } finally {
                this.$functions?.hideLoader();
            }
        },
        handleRequestPublicationClick() {
            const r = this.WebSkillRankingStore.ranking;

            if (
                !r.meta_title ||
                !r.meta_description ||
                (!r.domains?.vertical && !r.domains?.industry)
            ) {
                this.$message.error(
                    this.$trans("web_skill_ranking.missing_meta_or_domains"),
                );
                return;
            }

            const top10Profiles = this.WebSkillRankingStore.profiles
                .filter(p => p.current_position !== null && !p.is_excluded)
                .sort((a, b) => a.current_position - b.current_position)
                .slice(0, 10);

            if (top10Profiles.some((p) => !p.headline || !p.summary)) {
                this.$message.error(
                    this.$trans("web_skill_ranking.missing_profile_texts"),
                );
                return;
            }

            this.WebSkillRankingStore.openSubmitPublicationModal();
        },
        async handlePublicationConfirm() {
            try {
                this.$functions?.showLoader();
                await this.WebSkillRankingStore.requestPublication(
                    this.WebSkillRankingStore.ranking.id,
                );
                this.$message.success(
                    this.$trans("web_skill_ranking.publication_requested"),
                );
                window.location.reload();
            } catch (error) {
                if (error.response && error.response.status === 422 && error.response.data.fields) {
                    this.$message.error(
                        this.$trans("web_skill_ranking.missing_data") + ": " + error.response.data.fields.join(", ")
                    );
                } else {
                    this.$message.error(
                        this.$trans("web_skill_ranking.something_went_wrong"),
                    );
                }
            } finally {
                this.$functions?.hideLoader();
                this.WebSkillRankingStore.closeSubmitPublicationModal();
            }
        },
        handleAoEEdit() {
            if (!this.WebSkillRankingStore.ranking.domains?.vertical_name) {
                this.$message.warning(
                    this.$trans("web_skill_ranking.select_vertical_first"),
                );
                return;
            }
            this.WebSkillRankingStore.openEditAoEModal();
        },
        openRankingPath() {
            const path = this.WebSkillRankingStore.ranking.path;
            if (path) {
                window.open(path, "_blank");
            }
        },
    },
};
</script>

<style scoped>
.ranking-header-section {
    padding: 1.5rem;
    border: 1px solid #ddd;
    border-radius: 8px;
    background-color: #fff;
    margin-bottom: 2rem;
}
.header-main-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 2rem;
}
.info-row {
    display: flex;
    flex-wrap: wrap;
    gap: 1.5rem 2rem;
    margin-top: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid #eee;
}
.info-item {
    font-size: 0.95rem;
    color: #000;
    display: flex;
    align-items: center;
    white-space: nowrap;
}
.label-with-tooltip {
    display: inline-flex;
    align-items: center;
    font-weight: 600;
    font-size: 16px;
    color: #000;
}
.info-icon {
    font-size: 16px;
    margin-left: 4px;
    color: #41abc4;
    cursor: help;
}
.empty-text {
    color: #f56c6c;
    font-size: 0.9rem;
    margin-right: 5px;
}
.opacity-50 {
    opacity: 0.5;
}
.tooltip-content {
    max-width: 300px;
    font-size: 14px;
    font-weight: 600;
}
.stats-container {
    display: flex;
    align-items: center;
    gap: 2rem;
    flex: 1;
    flex-wrap: wrap;
}
.stat-item {
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
}
.label {
    font-size: 0.95rem;
    color: #000;
    font-weight: 700;
}
.value {
    font-size: 0.95rem;
    color: #000;
    font-weight: 700;
}
.status-sent {
    color: #67c23a;
}
.status-prequalified {
    color: #e6a23c;
}
.status-generated {
    color: #909399;
}
.button-group {
    display: flex;
    align-items: center;
    gap: 1rem;
}
.recalculate-btn {
    background-color: #41abc4;
    border-color: #41abc4;
    color: white;
    height: 36px;
    padding: 0 20px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 16px !important;
    font-weight: 700 !important;
}
.recalculate-btn:hover {
    background-color: #3693a9;
    border-color: #3693a9;
}
.request-publish-btn {
    background-color: #41abc4;
    border-color: #41abc4;
    color: white;
    height: 36px;
    padding: 0 20px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 16px !important;
    font-weight: 700 !important;
    margin-left: 0px !important;
}

.request-publish-btn:hover {
    background-color: #3693a9;
    border-color: #3693a9;
}
.ml-2 {
    margin-left: 8px;
}
.drupal-icon {
    width: 20px;
    height: auto;
    cursor: pointer;
    transition: opacity 0.2s;
    vertical-align: middle;
}

.drupal-icon:hover {
    opacity: 0.7;
}
</style>